import { aed } from "@/lib/format";
import { displayItemName } from "@/lib/menuLocale";
import {
  buildReportDocument,
  openReportPrint,
  periodMeta,
  resolveReportLetterhead,
  escapeReportHtml,
} from "@/lib/reportPrintShell";

function cell(value) {
  return value != null && value !== "" ? aed(value) : "";
}

async function buildHtml(title, report, tableHtml) {
  const company = await resolveReportLetterhead({ branchId: report?.branch_id, allBranches: report?.all_branches });
  return buildReportDocument({
    title,
    company,
    metaHtml: periodMeta(report),
    bodyHtml: tableHtml,
  });
}

function summaryBlock(summary) {
  if (!summary) return "";
  return `
    <h2>Summary</h2>
    <table>
      <tr><th>Bills / Items</th><th>Gross</th><th>Discount</th><th>Taxable</th><th>VAT</th><th>Round off</th><th>Net</th></tr>
      <tr>
        <td class="num">${summary.bill_count ?? summary.item_count ?? ""}</td>
        <td class="num">${cell(summary.gross_sales)}</td>
        <td class="num">${cell(summary.discount)}</td>
        <td class="num">${cell(summary.taxable_amount)}</td>
        <td class="num">${cell(summary.vat_collected)}</td>
        <td class="num">${cell(summary.round_off)}</td>
        <td class="num">${cell(summary.net_sales)}</td>
      </tr>
    </table>`;
}

export async function printDailySalesReport(report) {
  if (!report) return false;

  const byDateRows = (report.by_date || [])
    .map(
      (r) => `
      <tr>
        <td>${r.date}</td>
        <td class="num">${r.bill_count}</td>
        <td class="num">${cell(r.gross_sales)}</td>
        <td class="num">${cell(r.discount)}</td>
        <td class="num">${cell(r.taxable_amount)}</td>
        <td class="num">${cell(r.vat_collected)}</td>
        <td class="num">${cell(r.round_off)}</td>
        <td class="num">${cell(r.net_sales)}</td>
      </tr>`,
    )
    .join("");

  const billRows = (report.bills || [])
    .map(
      (b) => `
      <tr>
        <td class="mono">${escapeReportHtml(b.bill_number)}</td>
        <td>${escapeReportHtml(`${b.paid_date || ""} ${b.paid_time || ""}`.trim())}</td>
        <td>${escapeReportHtml(b.order_type_label || b.order_type)}</td>
        <td>${escapeReportHtml(b.counter_name || "")}</td>
        <td>${escapeReportHtml(b.customer_name || "")}</td>
        <td class="mono">${escapeReportHtml(b.customer_tax_number || "")}</td>
        <td class="num">${cell(b.round_off)}</td>
        <td class="num">${cell(b.net_amount)}</td>
      </tr>`,
    )
    .join("");

  const html = await buildHtml(
    "Daily Sales Summary",
    report,
    `${summaryBlock(report.summary)}
    <h2>By Date</h2>
    <table>
      <thead><tr><th>Date</th><th>Bills</th><th>Gross</th><th>Discount</th><th>Taxable</th><th>VAT</th><th>Round off</th><th>Net</th></tr></thead>
      <tbody>${byDateRows}</tbody>
    </table>
    <h2>Bills</h2>
    <table>
      <thead><tr><th>Bill</th><th>Paid</th><th>Type</th><th>Counter</th><th>Customer</th><th>TRN</th><th>Round off</th><th>Net</th></tr></thead>
      <tbody>${billRows}</tbody>
    </table>`,
  );

  return openReportPrint(html);
}

export async function printCounterSalesReport(report) {
  if (!report) return false;

  const rows = (report.rows || [])
    .map(
      (r) => `
      <tr>
        <td class="mono">${r.counter_code || ""}</td>
        <td>${r.counter_name}</td>
        <td class="num">${r.bill_count}</td>
        <td class="num">${cell(r.gross_sales)}</td>
        <td class="num">${cell(r.discount)}</td>
        <td class="num">${cell(r.vat_collected)}</td>
        <td class="num">${cell(r.round_off)}</td>
        <td class="num">${cell(r.net_sales)}</td>
      </tr>`,
    )
    .join("");

  const html = await buildHtml(
    "Counter Sales Summary",
    report,
    `${summaryBlock(report.summary)}
    <h2>By Counter</h2>
    <table>
      <thead><tr><th>Code</th><th>Counter</th><th>Bills</th><th>Gross</th><th>Discount</th><th>VAT</th><th>Round off</th><th>Net</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>`,
  );

  return openReportPrint(html);
}

export async function printItemSalesReport(report) {
  if (!report) return false;

  const rows = (report.rows || [])
    .map(
      (r) => `
      <tr>
        <td class="mono">${r.item_code || ""}</td>
        <td>${displayItemName(r)}</td>
        <td class="num">${r.qty}</td>
        <td class="num">${cell(r.gross_sales)}</td>
        <td class="num">${cell(r.discount)}</td>
        <td class="num">${cell(r.vat_amount)}</td>
        <td class="num">${cell(r.net_sales)}</td>
      </tr>`,
    )
    .join("");

  const html = await buildHtml(
    "Item Wise Sales",
    report,
    `${summaryBlock(report.summary)}
    <h2>Items</h2>
    <table>
      <thead><tr><th>Code</th><th>Item</th><th>Qty</th><th>Gross</th><th>Discount</th><th>VAT</th><th>Net</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>`,
  );

  return openReportPrint(html);
}

export async function printTaxReport(report) {
  if (!report) return false;

  const byDateRows = (report.by_date || [])
    .map(
      (r) => `
      <tr>
        <td>${r.date}</td>
        <td class="num">${r.bill_count}</td>
        <td class="num">${cell(r.taxable_amount)}</td>
        <td class="num">${cell(r.vat_collected)}</td>
        <td class="num">${cell(r.round_off)}</td>
        <td class="num">${cell(r.net_sales)}</td>
      </tr>`,
    )
    .join("");

  const byPaymentRows = (report.by_payment_method || [])
    .map(
      (r) => `
      <tr>
        <td>${r.payment_method}</td>
        <td class="num">${cell(r.amount)}</td>
      </tr>`,
    )
    .join("");

  const net = report.net || {};
  const input = report.input || {};
  const inputDateRows = (input.by_date || [])
    .map(
      (r) => `
      <tr>
        <td>${r.date}</td>
        <td class="num">${r.grn_count}</td>
        <td class="num">${cell(r.taxable_amount)}</td>
        <td class="num">${cell(r.vat_amount)}</td>
        <td class="num">${cell(r.invoice_total)}</td>
      </tr>`,
    )
    .join("");
  const inputDocRows = (input.rows || [])
    .map(
      (r) => `
      <tr>
        <td>${r.date}</td>
        <td>${r.receipt_no}</td>
        <td>${r.supplier_name || ""}</td>
        <td>${r.supplier_invoice_no || ""}</td>
        <td class="num">${r.vat_percent ?? ""}%</td>
        <td class="num">${cell(r.taxable_amount)}</td>
        <td class="num">${cell(r.vat_amount)}</td>
        <td class="num">${cell(r.invoice_total)}</td>
      </tr>`,
    )
    .join("");

  const html = await buildHtml(
    "VAT Input / Output",
    report,
    `<div><strong>Output VAT:</strong> ${cell(net.output_vat)} &nbsp; <strong>Return VAT:</strong> ${cell(net.return_vat)} &nbsp; <strong>Input VAT:</strong> ${cell(net.input_vat)} &nbsp; <strong>Net payable:</strong> ${cell(net.net_vat_payable)}</div>
    ${summaryBlock(report.summary)}
    <h2>Output VAT by Date</h2>
    <table>
      <thead><tr><th>Date</th><th>Bills</th><th>Taxable</th><th>Output VAT</th><th>Round off</th><th>Net</th></tr></thead>
      <tbody>${byDateRows}</tbody>
    </table>
    <h2>Output VAT by Payment Method</h2>
    <table>
      <thead><tr><th>Method</th><th>Amount</th></tr></thead>
      <tbody>${byPaymentRows}</tbody>
    </table>
    <h2>Input VAT by Date</h2>
    <table>
      <thead><tr><th>Date</th><th>GRNs</th><th>Taxable</th><th>Input VAT</th><th>Invoice total</th></tr></thead>
      <tbody>${inputDateRows || "<tr><td colspan='5'>No GRNs in period</td></tr>"}</tbody>
    </table>
    <h2>Input VAT — GRN Documents</h2>
    <table>
      <thead><tr><th>Date</th><th>GRN</th><th>Supplier</th><th>Invoice</th><th>VAT %</th><th>Taxable</th><th>Input VAT</th><th>Total</th></tr></thead>
      <tbody>${inputDocRows || "<tr><td colspan='8'>No GRNs in period</td></tr>"}</tbody>
    </table>`,
  );

  return openReportPrint(html);
}

export async function printSalesReport(report, type, options = {}) {
  const withBranch = report ? { ...report, branch_id: report.branch_id ?? options.branchId, all_branches: options.allBranches } : report;
  switch (type) {
    case "daily":
      return printDailySalesReport(withBranch);
    case "counter":
      return printCounterSalesReport(withBranch);
    case "items":
      return printItemSalesReport(withBranch);
    case "tax":
      return printTaxReport(withBranch);
    default:
      return false;
  }
}
